import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaCheck, FaCrown, FaRocket, FaStar } from 'react-icons/fa';
import { getSubscriptionPlans, upgradeSubscription } from '../services/api';

const Pricing = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pricingColumns, setPricingColumns] = useState(4);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    const updatePricingColumns = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setPricingColumns(1);
        return;
      }

      if (width <= 1024) {
        setPricingColumns(2);
        return;
      }

      setPricingColumns(4);
    };

    updatePricingColumns();
    window.addEventListener('resize', updatePricingColumns);
    return () => window.removeEventListener('resize', updatePricingColumns);
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await getSubscriptionPlans();
      console.log('Fetched plans:', response);
      console.log('API Response:', response);  // 👈 ADD THIS
    console.log('Type:', typeof response);    // 👈 ADD THIS

      setPlans(response.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planKey) => {
    if (!user) {
      setMessage({ type: 'error', text: 'Veuillez vous connecter pour changer de forfait' });
      return;
    }

    if (user.role !== 'photographer') {
      setMessage({ type: 'error', text: 'Seuls les photographes peuvent souscrire à un forfait' });
      return;
    }

    setUpgrading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await upgradeSubscription({ plan: planKey });
      setMessage({ type: 'success', text: response.data.message });
      
      // Update user data in localStorage
      const userData = JSON.parse(localStorage.getItem('user'));
      userData.subscription_plan = planKey;
      localStorage.setItem('user', JSON.stringify(userData));
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'La mise à niveau a échoué' });
    } finally {
      setUpgrading(false);
    }
  };

  const getPlanIcon = (planKey) => {
    switch(planKey) {
      case 'basic': return <FaCheck style={{ fontSize: '2rem' }} />;
      case 'pro': return <FaStar style={{ fontSize: '2rem' }} />;
      case 'enterprise': return <FaCrown style={{ fontSize: '2rem' }} />;
      default: return <FaRocket style={{ fontSize: '2rem' }} />;
    }
  };

  const getPlanColor = (planKey) => {
    switch(planKey) {
      case 'basic': return '#4CAF50';
      case 'pro': return '#FF8C42';
      case 'enterprise': return '#9C27B0';
      default: return '#757575';
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement des forfaits...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{ color: 'var(--primary-orange)', fontSize: '3rem', marginBottom: '1rem' }}>
          Choisissez votre forfait
        </h1>
        <p style={{ fontSize: '1.2rem', color: 'var(--gray)' }}>
          Développez votre activité photo avec des tarifs flexibles
        </p>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          {message.text}
        </div>
      )}

      {user && user.role === 'photographer' && (
        <div style={{
          padding: '1rem',
          background: 'var(--light-green)',
          borderRadius: '8px',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <strong style={{ color: 'var(--dark-green)' }}>
            Forfait actuel : {user.subscription_plan ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1) : 'Gratuit'}
          </strong>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${pricingColumns}, minmax(0, 1fr))`,
        gap: '2rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {plans && Object.entries(plans).map(([key, plan]) => {
          const isCurrentPlan = user && user.subscription_plan === key;
          const planColor = getPlanColor(key);

          return (
            <div
              key={key}
              style={{
                background: 'white',
                borderRadius: '15px',
                padding: '2rem',
                boxShadow: isCurrentPlan ? `0 8px 24px ${planColor}40` : '0 4px 12px rgba(0,0,0,0.1)',
                border: isCurrentPlan ? `3px solid ${planColor}` : '1px solid var(--light-gray)',
                position: 'relative',
                transition: 'all 0.3s ease',
                transform: key === 'pro' ? 'scale(1.05)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 28px ${planColor}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = key === 'pro' ? 'scale(1.05)' : 'scale(1)';
                e.currentTarget.style.boxShadow = isCurrentPlan ? `0 8px 24px ${planColor}40` : '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              {key === 'pro' && (
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--primary-orange)',
                  color: 'white',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                }}>
                  LE PLUS POPULAIRE
                </div>
              )}

              {isCurrentPlan && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: planColor,
                  color: 'white',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '15px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                  ACTUEL
                </div>
              )}

              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ color: planColor, marginBottom: '1rem' }}>
                  {getPlanIcon(key)}
                </div>
                <h2 style={{ color: planColor, marginBottom: '0.5rem', fontSize: '1.8rem' }}>
                  {plan.name}
                </h2>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--dark-gray)' }}>
                  {Number(plan.price || 0).toFixed(2)} F
                  <span style={{ fontSize: '1rem', color: 'var(--gray)' }}>/mois</span>
                </div>
              </div>

              <div style={{
                padding: '1.5rem 0',
                borderTop: '2px solid var(--light-gray)',
                borderBottom: '2px solid var(--light-gray)',
                marginBottom: '1.5rem'
              }}>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {plan.features.map((feature, index) => (
                    <li key={index} style={{
                      padding: '0.8rem 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.8rem',
                      color: 'var(--dark-gray)'
                    }}>
                      <FaCheck style={{ color: planColor, flexShrink: 0 }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {user && user.role === 'photographer' ? (
                <button
                  onClick={() => handleUpgrade(key)}
                  disabled={isCurrentPlan || upgrading || key === 'free'}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: isCurrentPlan ? 'var(--gray)' : planColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: isCurrentPlan || key === 'free' ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: isCurrentPlan || key === 'free' ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrentPlan && key !== 'free') {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {isCurrentPlan ? 'Forfait actuel' : key === 'free' ? 'Gratuit à vie' : upgrading ? 'Traitement...' : 'Mettre à niveau'}
                </button>
              ) : (
                <button
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: planColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.location.href = user ? '/dashboard' : '/register'}
                >
                  {user ? 'Commencer' : "S'inscrire"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: '4rem',
        padding: '3rem',
        background: 'linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-green) 100%)',
        borderRadius: '15px',
        color: 'white',
        textAlign: 'center'
      }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>
          Besoin d'un forfait personnalisé ?
        </h2>
        <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.95 }}>
          Contactez-nous pour des solutions entreprise adaptées à vos besoins
        </p>
        <button
          style={{
            background: 'white',
            color: 'var(--primary-orange)',
            padding: '1rem 3rem',
            border: 'none',
            borderRadius: '30px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
          onClick={() => window.location.href = 'mailto:sales@photomatch.com'}
        >
          Contacter l'équipe commerciale
        </button>
      </div>

      <div style={{
        marginTop: '3rem',
        padding: '2rem',
        background: 'var(--off-white)',
        borderRadius: '12px'
      }}>
        <h3 style={{ color: 'var(--primary-green)', marginBottom: '1.5rem', textAlign: 'center' }}>
          Questions fréquentes
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          <div>
            <h4 style={{ color: 'var(--primary-orange)', marginBottom: '0.5rem' }}>
              Puis-je changer de forfait à tout moment ?
            </h4>
            <p style={{ color: 'var(--gray)' }}>
              Oui ! Vous pouvez passer à un forfait supérieur ou inférieur à tout moment. Les changements prennent effet immédiatement.
            </p>
          </div>
          <div>
            <h4 style={{ color: 'var(--primary-orange)', marginBottom: '0.5rem' }}>
              Que deviennent mes données si je passe à un forfait inférieur ?
            </h4>
            <p style={{ color: 'var(--gray)' }}>
              Vos données existantes restent en sécurité. Les nouveaux imports seront simplement soumis aux limites du nouveau forfait.
            </p>
          </div>
          <div>
            <h4 style={{ color: 'var(--primary-orange)', marginBottom: '0.5rem' }}>
              Existe-t-il un essai gratuit ?
            </h4>
            <p style={{ color: 'var(--gray)' }}>
              Le forfait gratuit est disponible à vie ! Passez à un forfait supérieur lorsque vous avez besoin de plus de stockage ou d'événements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
